import type { FieldValues } from 'react-hook-form';

import type { Countries } from '@ctct/domain-layer';

import type { EventBus } from '../../../../helpers';
import type { BaseFormEvents } from '../types';

export class FormChangeHandlerService<T extends FieldValues> {
  constructor(
    private countries: Countries,
    private eventBus: EventBus<BaseFormEvents<T>>,
    private setIsDirty: (isDirty: boolean) => void,
    private handleClearErrors: () => void
  ) {}

  public api() {
    return {};
  }

  public data() {
    return {};
  }

  public handlers() {
    return {
      onIsDirtyChange: (isDirty: boolean) => {
        this.setIsDirty(isDirty);
        this.eventBus.emit('formDirtyStateChanged', isDirty, { source: 'user' });
      },

      onZipCodeChange: (
        zipcode: string,
        {
          setValue,
          getValues,
        }: {
          setValue: (name: keyof T, value: string | boolean) => void;
          getValues: (payload?: string | string[]) => T;
        }
      ) => {
        const { country } = getValues();

        setValue('zipcode', this.formatZipCode(zipcode, country));
      },

      onCountryChange: (
        selectedCountryCode: string,
        {
          setValue,
          defaultValues,
        }: {
          setValue: (name: keyof T, value: string | boolean) => void;
          defaultValues: T;
        }
      ) => {
        this.handleClearErrors();

        const setCity = (city: string) => setValue('city', city);
        const setState = (state: string) => setValue('state', state);
        const setZipcode = (zipcode: string) => setValue('zipcode', zipcode);
        const setAddress1 = (address1: string) => setValue('address1', address1);
        const setAddress2 = (address2: string) => setValue('address2', address2);
        const setCurrentTaxAgreement = (value: boolean) => setValue('currentTaxAgreement', value);
        const setVatNumber = (value: string) => setValue('vatNumber', value);

        if (defaultValues.country === selectedCountryCode) {
          setCity(defaultValues.city);
          setState(defaultValues.state ?? '');
          setZipcode(defaultValues.zipcode);
          setAddress1(defaultValues.address1);
          setAddress2(defaultValues.address2 ?? '');
          setCurrentTaxAgreement(defaultValues.currentTaxAgreement ?? false);
          setVatNumber(defaultValues.vatNumber ?? '');
        } else {
          setCity('');
          setState('');
          setZipcode('');
          setAddress1('');
          setAddress2('');
          setCurrentTaxAgreement(false);
          setVatNumber('');
        }

        if (selectedCountryCode) {
          this.eventBus.emit(
            'countryUpdated',
            selectedCountryCode,
            this.countries.isB2bNotificationCountry(selectedCountryCode),
            { source: 'user' }
          );
          this.eventBus.emit('isVatEuCountry', this.countries.getIsVatEu(selectedCountryCode), {
            source: 'user',
          });
        } else {
          this.eventBus.emit('isVatEuCountry', false, { source: 'user' });
        }
      },
    };
  }

  private formatZipCode(zipCode: string, country: string): string {
    // Remove non-alphanumeric characters for countries like GB, CA (handles spaces, dashes, etc.)
    let cleanedZip = zipCode.replace(/[^A-Za-z0-9]/g, '').toUpperCase(); // Removes everything except letters and digits

    switch (country.toUpperCase()) {
      case 'GB': // UK postcode format (e.g., SW1A 1AA)
        if (cleanedZip.length <= 4) {
          return cleanedZip; // Allow typing up to the first 4 characters
        }

        if (cleanedZip.length > 7) {
          cleanedZip = cleanedZip.slice(0, 7); // Limit to max 7 characters (ignoring spaces)
        }

        return `${cleanedZip.slice(0, cleanedZip.length - 3)} ${cleanedZip.slice(cleanedZip.length - 3)}`;

      case 'AU': // Australian postcode (4 digits, e.g., 2000)
      case 'NZ': // New Zealand postcode (4 digits, e.g., 6011)
        return cleanedZip.slice(0, 4); // Limit to 4 digits

      case 'US': // US ZIP code (5 digits, optional 4 digits, e.g., 12345 or 12345-6789)
        if (cleanedZip.length <= 5) {
          return cleanedZip; // Return first 5 digits
        }

        if (cleanedZip.length > 9) {
          cleanedZip = cleanedZip.slice(0, 9); // Limit to max 9 digits
        }

        return `${cleanedZip.slice(0, 5)}-${cleanedZip.slice(5)}`; // Insert dash after 5 digits

      case 'CA': // Canadian postcode format (e.g., K1A 0B1)
        cleanedZip = cleanedZip.slice(0, 6); // Limit to 6 alphanumeric characters

        if (cleanedZip.length <= 3) {
          return cleanedZip; // Allow typing up to 3 characters
        }

        return `${cleanedZip.slice(0, 3)} ${cleanedZip.slice(3)}`; // Add space after 3 characters

      case 'IN': // Indian postcode (6 digits, e.g., 110001)
        return cleanedZip.slice(0, 6); // Limit to 6 digits

      default:
        return zipCode; // Return as-is for unsupported countries
    }
  }
}
